import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Database } from "@/integrations/supabase/types";
import { FEATURES } from "@/lib/featureFlags";
import { setSentryRole } from "@/lib/sentry";
import { analytics } from "@/lib/analytics";

type AppRole = Database["public"]["Enums"]["app_role"];

export const useUserRole = () => {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  // Tag every Sentry event with the resolved role so errors can be sliced by audience, and set it
  // as a PostHog person property so analytics can segment customer vs technician vs admin.
  useEffect(() => {
    setSentryRole(role);
    if (role) analytics.setPersonProperties({ role });
  }, [role]);

  useEffect(() => {
    const fetchRole = async () => {
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (error) {
          console.error("Error fetching role:", error);
          setRole("user");
        } else if (!data || data.length === 0) {
          setRole("user");
        } else {
          // Priority: admin > property_manager > technician > user
          const rolePriority: AppRole[] = ["admin", "property_manager", "technician", "user"];
          const roles = data.map((d) => d.role);
          let bestRole = rolePriority.find((r) => roles.includes(r)) || "user";
          // PM hidden in v1: a stray property_manager account behaves as a customer
          // (keeps routing/access consistent and avoids redirect loops on /dashboard).
          if (!FEATURES.propertyManager && bestRole === "property_manager") {
            bestRole = "user";
          }
          setRole(bestRole);
        }
      } catch (err) {
        console.error("Error fetching role:", err);
        setRole("user");
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchRole();
    }
  }, [user, authLoading]);

  const isAdmin = role === "admin";
  const isTechnician = role === "technician";
  const isPropertyManager = role === "property_manager";
  const isUser = role === "user";

  const getDashboardPath = () => {
    switch (role) {
      case "technician":
        return "/tech";
      case "property_manager":
        // PM hidden in v1: route stray PM accounts to the customer dashboard
        return FEATURES.propertyManager ? "/pm/dashboard" : "/dashboard";
      case "admin":
        return "/admin";
      default:
        return "/dashboard";
    }
  };

  return {
    role,
    loading: loading || authLoading,
    isAdmin,
    isTechnician,
    isPropertyManager,
    isUser,
    getDashboardPath,
  };
};
